import { useCallback, useEffect, useMemo, useState } from "react";
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from "@/components/ui/resizable";
import { TooltipProvider } from "@/components/ui/tooltip";
import { DayPaneScroller } from "./day-view/day-pane-scroller";
import { DayViewPanelCollapsed } from "./day-view/day-view-panel-collapsed";
import { DayViewRightPanel } from "./day-view/day-view-right-panel";
import {
  persistSplitLayout,
  readDayViewSplitLayout,
  readRightPanelOpen,
  RIGHT_PANEL_OPEN_KEY,
} from "./day-view/day-view-utils";
import { useDayViewClock } from "./day-view/use-day-view-clock";
import { useDayViewColumns } from "./day-view/use-day-view-columns";
import { useDayViewSelection } from "./day-view/use-day-view-selection";
import { useIsMdUp } from "./day-view/use-is-md-up";
import { toDateKey } from "./calendar-utils";

const EMPTY_EVENTS = [];

function DayView({
  selectedDate,
  events,
  /** Second day pane ("2 days" split): its date, its events, and its handlers. */
  splitDate = null,
  splitDayEvents = EMPTY_EVENTS,
  onSelectSplitDate,
  onCloseSplit,
  eventsByDate,
  onSelectDate,
  onNavigateMonth,
  use12HourFormat = false,
  zoomPercent = 100,
  widthZoomPercent = 100,
  timeAxis,
  initialScrollToNow = true,
  initialScrollRatio = null,
  initialSelectedEventId = null,
  /** Deep-linked appointment: scrolled to on every `focusSignal` bump and ringed while set. */
  focusEventId = null,
  focusSignal = 0,
  highlightedEventId = null,
  onDismissHighlight,
  onSaveScrollRatio,
  onDidInitialScroll,
  onSlotsChanged,
  registerViewport,
  zoomAnchorRef,
  resetSignal = 0,
  tenantOptions,
  doctorOptions,
  timeOffBlocks,
  /** Selected tenants or doctors (per groupBy) rendered as titled side-by-side columns; null = one untitled column. */
  dayViewColumns = null,
  dayViewGroupBy = "tenant",
  /** Ids of the active standalone calendars — keeps their events out of doctor/clinic columns. */
  standaloneIdSet = null,
  /** ({ calendarId, dayKey, minute }) → open the "New entry" sheet for a standalone-calendar column. */
  onCreateCalendarEntry,
  /** Snapped landing preview while an appointment drag is in flight. */
  dragPlaceholder = null,
  /** "dynamic" | "comfortable" | "spacious" | "custom" — per-column min width. */
  columnWidthMode = "dynamic",
  /** Pixel width used when columnWidthMode === "custom". */
  columnWidthCustomPx,
  /** Session rail (background who-is-where indicator) view preferences. */
  showSessionRail = false,
  railWidthPx = 26,
  railColorMode = "accent",
  railMarginPx = 0,
  /** (columnId, paneDate) → open the session override dialog (doctor grouping only). */
  onSessionAction,
}) {
  const [rightPanelOpen, setRightPanelOpen] = useState(readRightPanelOpen);
  const isMdUp = useIsMdUp();
  // Which pane owns zoom/pan (the last one the cursor entered) — drives the
  // active-pane highlight in split mode. Defaults to the primary day.
  const [activePaneKey, setActivePaneKey] = useState("primary");

  useEffect(() => {
    try {
      window.localStorage.setItem(RIGHT_PANEL_OPEN_KEY, rightPanelOpen ? "1" : "0");
    } catch {
      /* ignore */
    }
  }, [rightPanelOpen]);

  // Layout for the calendar-vs-details divider only (persisted). The two-day
  // divider is intentionally NOT persisted — it resets to 50/50 each open.
  const splitDefaultLayout = useMemo(() => readDayViewSplitLayout(), []);

  const hasSplitDay = splitDate != null;
  // A fresh split always starts with the primary day active.
  useEffect(() => {
    if (!hasSplitDay) setActivePaneKey("primary");
  }, [hasSplitDay]);
  const primaryColumns = useDayViewColumns(events, dayViewColumns, dayViewGroupBy, standaloneIdSet);
  const splitDayColumns = useDayViewColumns(
    hasSplitDay ? splitDayEvents : EMPTY_EVENTS,
    dayViewColumns,
    dayViewGroupBy,
    standaloneIdSet,
  );

  // One pane per visible day. The second pane carries its own date handlers so
  // its self-contained header can render per-pane navigation and a close X.
  const panes = useMemo(() => {
    const primaryPane = {
      key: "primary",
      date: selectedDate,
      dayKey: toDateKey(selectedDate),
      splitColumns: primaryColumns.splitColumns,
      columnsToRender: primaryColumns.columnsToRender,
      onDateChange: onSelectDate,
    };
    if (!hasSplitDay) {
      return [primaryPane];
    }
    return [
      primaryPane,
      {
        key: "split",
        date: splitDate,
        dayKey: toDateKey(splitDate),
        splitColumns: splitDayColumns.splitColumns,
        columnsToRender: splitDayColumns.columnsToRender,
        onDateChange: onSelectSplitDate,
        onClose: onCloseSplit,
      },
    ];
  }, [
    selectedDate,
    primaryColumns,
    onSelectDate,
    hasSplitDay,
    splitDate,
    splitDayColumns,
    onSelectSplitDate,
    onCloseSplit,
  ]);

  // Selection spans both panes so the details panel follows clicks on either day.
  const sortedEvents = useMemo(
    () =>
      hasSplitDay
        ? [...primaryColumns.sortedEvents, ...splitDayColumns.sortedEvents].sort(
            (left, right) => left.startAt.toMillis() - right.startAt.toMillis(),
          )
        : primaryColumns.sortedEvents,
    [hasSplitDay, primaryColumns.sortedEvents, splitDayColumns.sortedEvents],
  );

  const { selectedEventId, setSelectedEventId, selectedEvent } = useDayViewSelection(
    sortedEvents,
    initialSelectedEventId,
  );

  // A deep-linked appointment also becomes the selection so the details panel follows the
  // jump — useDayViewSelection only reads its initial value at mount, and this pane is
  // usually already mounted when the request arrives.
  useEffect(() => {
    if (highlightedEventId) {
      setSelectedEventId(highlightedEventId);
    }
  }, [highlightedEventId, setSelectedEventId]);

  // Clicking a different appointment means the user has moved on — retire the highlight.
  const handleSelectEvent = useCallback(
    (eventId) => {
      setSelectedEventId(eventId);
      if (highlightedEventId && eventId !== highlightedEventId) {
        onDismissHighlight?.();
      }
    },
    [highlightedEventId, onDismissHighlight, setSelectedEventId],
  );

  const { currentTimeRowStart, currentTimeRowOffsetPercent } = useDayViewClock(timeAxis);

  // Props shared by every pane's scroller (body + zoom/pan wiring). Per-pane
  // props (pane, showHeader, isPrimary, className, initial-scroll, zoomAnchorRef)
  // are supplied at the call sites below.
  const paneCommon = {
    registerViewport,
    zoomPercent,
    widthZoomPercent,
    resetSignal,
    use12HourFormat,
    timeAxis,
    selectedEventId,
    setSelectedEventId: handleSelectEvent,
    highlightedEventId,
    currentTimeRowStart,
    currentTimeRowOffsetPercent,
    timeOffBlocks,
    doctorOptions,
    columnWidthMode,
    columnWidthCustomPx,
    dayViewGroupBy,
    dragPlaceholder,
    showSessionRail,
    railWidthPx,
    railColorMode,
    railMarginPx,
    onSessionAction,
    onCreateCalendarEntry,
  };

  // The calendar area is either a single pane or, in "2 days" mode, two panes in
  // their own scroll containers behind a draggable divider. `fillClass` sizes the
  // outermost element to its slot (a flex child, or a fixed-height panel).
  const renderCalendarArea = (fillClass) => {
    if (!hasSplitDay) {
      return (
        <DayPaneScroller
          {...paneCommon}
          pane={panes[0]}
          paneIndex={0}
          isPrimary
          className={fillClass}
          zoomAnchorRef={zoomAnchorRef}
          initialScrollToNow={initialScrollToNow}
          initialScrollRatio={initialScrollRatio}
          initialSelectedEventId={initialSelectedEventId}
          focusEventId={focusEventId}
          focusSignal={focusSignal}
          onSaveScrollRatio={onSaveScrollRatio}
          onDidInitialScroll={onDidInitialScroll}
        />
      );
    }
    return (
      // No defaultLayout/onLayoutChanged → mounts fresh at 50/50 every time.
      <ResizablePanelGroup orientation="horizontal" className={fillClass}>
        <ResizablePanel id="day-view-pane-primary" defaultSize="50" minSize="20" className="min-w-0">
          <DayPaneScroller
            {...paneCommon}
            pane={panes[0]}
            paneIndex={0}
            isPrimary
            showHeader
            isActive={activePaneKey === "primary"}
            onActivate={() => setActivePaneKey("primary")}
            className="h-full min-h-0 min-w-0"
            // Split mode: no cursor anchor — each pane keeps its own view
            // centered on zoom (the shared anchor can't drive two panes at once).
            zoomAnchorRef={null}
            initialScrollToNow={initialScrollToNow}
            initialScrollRatio={initialScrollRatio}
            initialSelectedEventId={initialSelectedEventId}
            focusEventId={focusEventId}
            focusSignal={focusSignal}
            onSaveScrollRatio={onSaveScrollRatio}
            onDidInitialScroll={onDidInitialScroll}
          />
        </ResizablePanel>
        <ResizableHandle withHandle />
        <ResizablePanel id="day-view-pane-split" defaultSize="50" minSize="20" className="min-w-0">
          <DayPaneScroller
            {...paneCommon}
            pane={panes[1]}
            paneIndex={1}
            showHeader
            isActive={activePaneKey === "split"}
            onActivate={() => setActivePaneKey("split")}
            className="h-full min-h-0 min-w-0"
            zoomAnchorRef={null}
            initialScrollToNow
            initialScrollRatio={null}
            initialSelectedEventId={null}
          />
        </ResizablePanel>
      </ResizablePanelGroup>
    );
  };

  return (
    <div className="isolate flex h-full min-h-0 overflow-hidden bg-background">
      {rightPanelOpen && isMdUp ? (
        <ResizablePanelGroup
          orientation="horizontal"
          defaultLayout={splitDefaultLayout}
          onLayoutChanged={persistSplitLayout}
          className="flex h-full min-h-0 w-full flex-1"
        >
          <ResizablePanel id="day-view-calendar" defaultSize="62" minSize="40" className="min-w-0">
            {renderCalendarArea("h-full min-h-0 min-w-0")}
          </ResizablePanel>
          <ResizableHandle withHandle />
          <ResizablePanel
            id="day-view-details"
            defaultSize="400px"
            minSize="400px"
            maxSize="800px"
            className="min-w-0"
          >
            <TooltipProvider delayDuration={400}>
              <DayViewRightPanel
                setRightPanelOpen={setRightPanelOpen}
                selectedDate={selectedDate}
                eventsByDate={eventsByDate}
                onSelectDate={onSelectDate}
                onNavigateMonth={onNavigateMonth}
                selectedEvent={selectedEvent}
                use12HourFormat={use12HourFormat}
              />
            </TooltipProvider>
          </ResizablePanel>
        </ResizablePanelGroup>
      ) : (
        <>
          {renderCalendarArea("min-h-0 min-w-0 flex-1")}
          {!rightPanelOpen && isMdUp ? (
            <TooltipProvider delayDuration={400}>
              <DayViewPanelCollapsed setRightPanelOpen={setRightPanelOpen} />
            </TooltipProvider>
          ) : null}
        </>
      )}
    </div>
  );
}

export default DayView;
